import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { removeNullUndefinedEmptyFromObject } from '../../../helpers/common';
import { fetchSearchResults } from '../../../new-lib/apis';
import { useDispatch } from 'react-redux';
import { setSearchResults } from '../../../redux/slices/searchResultsSlice';
import { SearchBodyInterface, SearchResults } from '../../search-new/types';
import { useSearchContext } from '../../search-new/context/SearchContext';
import { useQuery } from 'react-query';

const useExploreSearch = (
  entityId: number,
  pageNumber?: number,
  exploreLocation?: string,
  loadedSearchResults?: any
): SearchBodyInterface => {
  const dispatch = useDispatch();
  const { locale } = useRouter();
  const [enabled, setEnabled] = useState(false);
  const { searchFiltersState, appliedFiltersCount } = useSearchContext();

  useEffect(() => {
    if (
      appliedFiltersCount > 0 ||
      (pageNumber !== undefined && pageNumber !== 1) ||
      (searchFiltersState.searchFilters.sort_by.order_by !== '' &&
        searchFiltersState.searchFilters.sort_by.order_direction !== '')
    ) {
      setEnabled(true);
    }
  }, [searchFiltersState.searchFilters, pageNumber, appliedFiltersCount]);

  const { isLoading, isError, data } = useQuery({
    queryKey: [
      'nawyNowSearch',
      searchFiltersState.searchFilters,
      entityId,
      pageNumber,
      locale
    ],
    queryFn: async ({ signal }) =>
      fetchSearchResults(
        removeNullUndefinedEmptyFromObject({
          ...searchFiltersState.searchBody,
          start: pageNumber ? (pageNumber - 1) * 12 + 1 : 1
        }),
        locale,
        { signal }
      ),
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    keepPreviousData: true,
    enabled,
    initialData: loadedSearchResults
  });

  const getLoadedSearchResults = (): SearchResults => {
    if (exploreLocation == 'nawy_now_page') return data?.data?.values;

    return searchFiltersState.searchBody.show == 'property'
      ? data?.data?.values
      : data?.results;
  };

  const getLoadedSearchResultsCount = () => {
    return searchFiltersState.searchBody.show == 'property'
      ? data?.data?.total_properties
      : data?.total;
  };

  const searchBodyResults: SearchBodyInterface = {
    isFetching: isLoading,
    isLoading: isLoading,
    isError: isError,
    loadedSearchResults: getLoadedSearchResults(),
    loadedSearchResultsCount: getLoadedSearchResultsCount(),
    loadedPropertyTypes: data?.data?.property_types,
    loadedAmenities: data?.data?.amenities,
    firstRenderSSR: false,
    ssrSearchType:
      searchFiltersState.searchBody.show == 'property'
        ? 'properties'
        : 'compounds'
  };
  dispatch(setSearchResults(searchBodyResults));

  return searchBodyResults;
};

export default useExploreSearch;
